import React from 'react';
import P from '@hero/ui-kit/typography/P';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import Circle from '@hero/ui-kit/graphics/Circle';
import useUpdateLeadApprovalStatus from '../../api/useUpdateLeadApprovalStatus';
import getApprovalSubStatus from '../../utils/getApprovalSubStatus';
import { AllApprovalStatusSubStates, ApprovalStatus, RejectedSubStates } from '../../types';

// HWM-22633: reject mirrors enroll — picking "Reject" in the Change status
// (New activity) modal routes here instead of POSTing a status change. The
// backend requires a mandatory reject sub-status (reason); these are the five
// canonical values (see `RejectedSubStates` + getApprovalSubStatus REJECTED).
const REJECT_REASONS: RejectedSubStates[] = [
    'NO_INSURANCE',
    'DUPLICATE',
    'STALE',
    'NOT_INTERESTED',
    'NO_NECESSITY'
];

interface RejectFormFillModalProps {
    leadId: string;
    leadName?: string;
    onSuccess: () => void;
    onCancel: () => void;
}

const RejectFormFillModal: React.FC<RejectFormFillModalProps> = ({
    leadId,
    leadName,
    onSuccess,
    onCancel
}) => {
    const [reason, setReason] = React.useState<RejectedSubStates | ''>('');
    const { mutate, isPending, isError } = useUpdateLeadApprovalStatus(onSuccess);

    const handleReject = () => {
        if (!reason) return;
        mutate({
            payload: {
                lead_id: leadId,
                approval_status: 'REJECTED' as ApprovalStatus,
                approval_sub_status: reason as AllApprovalStatusSubStates
            }
        });
    };

    return (
        <>
            <P
                strong
                centered
                noDefaultMargin
                styles={{ marginBottom: '1.6rem', fontSize: '3.2rem', marginTop: '2rem' }}
            >
                Reject form fill
            </P>
            <P centered noDefaultMargin styles={{ marginBottom: '2.4rem' }}>
                Are you sure you want to reject{leadName ? ` ${leadName}` : ''}? Select a reason
                below.
            </P>
            <Section centered noDefaultPadding styles={{ marginBottom: '2.4rem' }}>
                <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '0.8rem' }}>
                    {REJECT_REASONS.map((value) => {
                        const label = getApprovalSubStatus(
                            value as AllApprovalStatusSubStates,
                            'REJECTED'
                        ).label;
                        const selected = reason === value;
                        return (
                            <ClickableOpacity
                                key={value}
                                alt={`Select ${label}`}
                                onClick={() => setReason(value)}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1.2rem'
                                    }}
                                >
                                    {selected ? (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '2.4rem',
                                                height: '2.4rem',
                                                borderRadius: '100%',
                                                border: '0.2rem solid var(--color-primary-beta)'
                                            }}
                                        >
                                            <Circle size="tiny" />
                                        </div>
                                    ) : (
                                        <Circle disabled inverted size="small" />
                                    )}
                                    <span>{label}</span>
                                </div>
                            </ClickableOpacity>
                        );
                    })}
                </div>
            </Section>
            {isError ? (
                <P
                    centered
                    noDefaultMargin
                    styles={{ marginBottom: '1.6rem', color: 'var(--color-fail-alpha)' }}
                >
                    Something went wrong. Please try again or contact customer support.
                </P>
            ) : null}
            <Section centered noDefaultPadding>
                <div style={{ display: 'flex', alignItems: 'center', columnGap: '2.4rem' }}>
                    <Button variant="outlineSecondary" onClick={onCancel} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button onClick={handleReject} disabled={!reason || isPending}>
                        Reject
                    </Button>
                </div>
            </Section>
        </>
    );
};

export default RejectFormFillModal;
